import { isNumber } from "@omnia/shared/utils/utils";
import { SeedRateUnit } from "./generated/enums";

type UnitType =
    | "weight"
    | "seedCount"
    | "packs"

type WeightUnit =
    | SeedRateUnit.Kg
    | SeedRateUnit.Tonnes

type SeedCountUnit =
    | SeedRateUnit.Seeds
    | SeedRateUnit.Seeds_1k
    | SeedRateUnit.Seeds_10k
    | SeedRateUnit.Seeds_1m
    | SeedRateUnit.Units
    | SeedRateUnit.Bulbs
    | SeedRateUnit.Plants
    | SeedRateUnit.Tubers

type PackUnit =
    | SeedRateUnit.Bags
    | SeedRateUnit.Trays

export type SeedRateUnitInfo =
    {
        unit: SeedRateUnit

        labels: {
            label: string
            rate: string
        }

        type: UnitType

    }
    & (
        | {
            unit: PackUnit
            type: "packs"
        }
        | {
            unit: WeightUnit
            type: "weight"
            conversionFactorToKg: number
        }
        | {
            unit: SeedCountUnit
            type: "seedCount"
            conversionFactorToSeed: number
        }
    )

export const seedRateUnits: SeedRateUnitInfo[] = [

    getWeightUnit(SeedRateUnit.Kg, "kg", 1),
    getWeightUnit(SeedRateUnit.Tonnes, "tonnes", 1000),

    getSeedCountUnit(SeedRateUnit.Units, "units", 50000),
    getPacksUnit(SeedRateUnit.Bags, "bags"),
    getSeedCountUnit(SeedRateUnit.Plants, "plants", 1),
    getPacksUnit(SeedRateUnit.Trays, "trays"),

    getSeedCountUnit(SeedRateUnit.Seeds, "seeds", 1),
    getSeedCountUnit(SeedRateUnit.Seeds_1k, "1k seeds", 1000),
    getSeedCountUnit(SeedRateUnit.Seeds_10k, "10K seeds", 10000),
    getSeedCountUnit(SeedRateUnit.Seeds_1m, "1m seeds", 1000000),

    getSeedCountUnit(SeedRateUnit.Tubers, "tubers", 1),
    getSeedCountUnit(SeedRateUnit.Bulbs, "bulbs", 1),

] as const

const seedRateUnitsMap = Object.fromEntries(seedRateUnits.map(u => [u.unit, u] as const)) as { [key in SeedRateUnit]: SeedRateUnitInfo }

export function getSeedRateUnitInfo(unit: SeedRateUnit | undefined) {
    return unit
        ? seedRateUnitsMap[unit]
        : seedRateUnitsMap[SeedRateUnit.Kg]
}

function getWeightUnit<U extends WeightUnit>(unit: U, label: string, conversionFactorToKg: number): SeedRateUnitInfo {
    return {
        unit,
        type: "weight",
        labels: {
            label,
            rate: label + "/ha"
        },
        conversionFactorToKg
    }
}

function getPacksUnit<U extends PackUnit>(unit: U, label: string): SeedRateUnitInfo {
    return {
        unit,
        type: "packs",
        labels: {
            label,
            rate: label + "/ha"
        }
    }
}

function getSeedCountUnit<U extends SeedCountUnit>(unit: U, label: string, conversionFactorToSeed: number): SeedRateUnitInfo {
    return {
        unit,
        type: "seedCount",
        labels: {
            label,
            rate: label + "/ha"
        },
        conversionFactorToSeed
    }
}


export function getTotalQtyLabel(qty: number, unit: SeedRateUnitInfo | SeedRateUnit, short = false) {

    if (qty === undefined) {
        return
    }

    unit ||= SeedRateUnit.Kg

    unit = isNumber(unit)
        ? getSeedRateUnitInfo(unit)
        : unit

    const u = (unit.type === "weight") && (!short)
        ? `${unit.labels.label} of seed`
        : unit.labels.label

    return `${qty.toLocaleString(undefined, { maximumFractionDigits: 3 })} ${u}`

}